// React
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
// MUI
import { Stack, Divider } from "@mui/material";
// Others
import { spinning } from "../../../merchants/offline-merchant-detail/utils";
import { useSnackbar } from "notistack";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import EmployeeTrxConfig from "../component/EmployeeTrxConfig";
import EmployeeAdvancedConfig from "../component/EmployeeAdvancedConfig";
import EmployeeOptionConfig from "../component/EmployeeOptionConfig";
import EmployeeFunctionAccControlConfig from "../component/EmployeeFunctionAccControlConfig";
import EmployeeDeveloperConfig from "../component/EmployeeDeveloperConfig";

// Model
import { updateEmployeeConfig } from "../model";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { formPreventEnterSubmit } from "utils/utils";

const EMPLOYEE_LOCALE_KEY = "page.employees.";

const EmployeeConfig = ({ employeeConfig, fetchEmployeeConfig }) => {
  const [isLoading, setIsLoading] = useState(false);
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit, reset } = formProps
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  useEffect(() => {
    reset(employeeConfigSpecialHandling(employeeConfig, "fetch"));
  }, [employeeConfig]);

  const onSubmit = async (formValue) => {
    try {
      setIsLoading(true);
      const finalFormValue = employeeConfigSpecialHandling(formValue, "update");
      const updateEmployeeConfigResp =
        await updateEmployeeConfig(finalFormValue);
      if (updateEmployeeConfigResp?.data?.responseCode === "0000") {
        snackbar(
          `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateSuccess")}`,
          "success",
        );
      }
      await initialFetch();
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed")}`,
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const buttons = () => (
    <Stack direction="row" justifyContent="flex-end" gap={2} flexWrap="wrap">
      <ButtonMAS type="submit" isLoading={isLoading} disabled={isLoading}>
        {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.update`)}
      </ButtonMAS>
    </Stack>
  );

  const ACCESS_CONTROL_MAPPINGS = {
    Void: "accessControlVoid",
    Refund: "accessControlRefund",
    Auth: "accessControlAuthorization",
    Completion: "accessControlAuthComplete",
    History: "accessControlHistory",
    Settlement: "accessControlSettlement",
    Settings: "accessControlSetting",
    AuthTopUp: "accessControlAuthTopUp",
    TipsAdj: "accessControlTipsAdjustment",
    ScreenSaver: "accessControlScreenSaver",
  };

  const CARD_TYPE_MAPPINGS = {
    visa: "dualBranchEnableVisa",
    master: "dualBranchEnableMaster",
    amex: "dualBranchEnableAmex",
    jcb: "dualBranchEnableJcb",
  };

  const METHOD_DISPLAY_FIELDS = [
    "methodCredit",
    "methodCash",
    "methodWechat",
    "methodAlipay",
    "methodQRpay",
    "methodOctopus",
    "methodQuickpass",
    "methodNFC",
    "methodShowQR",
    "methodScanQR",
  ]

  const employeeConfigSpecialHandling = (employeeConfig, type) => {
    if (type === "fetch") {
      const config = { ...employeeConfig };

      // Handle param "optDualBrandCardType"
      if (config.optDualBrandCardType) {
        const cardTypes = config.optDualBrandCardType.toLowerCase().split(",");
        cardTypes.forEach((type) => {
          if (CARD_TYPE_MAPPINGS[type]) {
            config[CARD_TYPE_MAPPINGS[type]] = true;
          }
        });
      }

      // Handle param "opPassword"
      if (!config.opPassword) return config;

      const opPasswordObj = config.opPassword.split(",").reduce((acc, pair) => {
        const [key, value] = pair.split("=");
        if (key && value) acc[key] = value;
        return acc;
      }, {});

      Object.entries(ACCESS_CONTROL_MAPPINGS).forEach(([shortKey, fullKey]) => {
        if (opPasswordObj[shortKey] !== undefined) {
          config[fullKey] = opPasswordObj[shortKey];
        }
      });

      // pfaas: handle payment method display fields
      METHOD_DISPLAY_FIELDS.forEach((field) => {
        if (config[field] != null) {
          config[field] = Boolean(config[field]);
        }
      })

      return config;
    }
    if (type === "update") {
      const formValue = JSON.parse(JSON.stringify(employeeConfig));

      // Map access control fields
      Object.entries(ACCESS_CONTROL_MAPPINGS).forEach(([shortKey, fullKey]) => {
        if (formValue[fullKey] !== undefined) {
          formValue[shortKey] = formValue[fullKey];
        }
      });

      // Build opPassword string
      formValue.opPassword = Object.entries(ACCESS_CONTROL_MAPPINGS)
        .reduce((acc, [shortKey, fullKey]) => {
          if (formValue[fullKey] !== undefined) {
            acc.push(`${shortKey}=${formValue[fullKey]}`);
            delete formValue[shortKey];
            delete formValue[fullKey];
          }
          return acc;
        }, [])
        .join(",");

      // Handle "optDualBrandCardType"
      formValue.optDualBrandCardType = Object.entries(CARD_TYPE_MAPPINGS)
        .filter(([, value]) => {
          const isThatCardTypeEnabled = formValue[value];
          delete formValue[value];
          return isThatCardTypeEnabled;
        })
        .map(([key]) => key)
        .join(",");

      // pfaas: handle payment method display fields
      METHOD_DISPLAY_FIELDS.forEach((field) => {
        if (formValue[field] != null) {
          formValue[field] = Number(formValue[field]);
        }
      })

      formValue.staticQrEnabledList = JSON.parse(
        formValue?.staticQrEnabledList ?? "[]",
      );
      formValue.optDcc = !!formValue?.opt_dcc;
      formValue.optDccSingapore = !!formValue?.opt_dcc_singapore;
      formValue.optIpp = !!formValue?.opt_ipp;
      formValue.optUnlinkRefund = !!formValue?.opt_unlink_refund;
      return formValue;
    }

    return employeeConfig;
  };

  const initialFetch = async () => {
    try {
      await fetchEmployeeConfig();
    } catch (error) {
      console.error("Error fetching employee detail:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.fetchEmployeeConfigFail")}`,
        "error",
      );
    }
  };

  return isLoading ? (
    spinning()
  ) : (
    <Stack direction="column" sx={{ background: "white" }}>
      <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
        <FormBuilder.Section>
          <EmployeeTrxConfig formProps={formProps} />
          <Divider />
          <EmployeeAdvancedConfig formProps={formProps} />
          <Divider />
          {/* <EmployeeDualBrandCardConfig formProps={formProps} /> */}
          {/* <Divider /> */}
          <EmployeeOptionConfig formProps={formProps} />
          <Divider />
          <EmployeeFunctionAccControlConfig formProps={formProps} />
          <Divider />
          <EmployeeDeveloperConfig formProps={formProps} />
          <Divider />
          {/* <EmployeeKioskOption formProps={formProps} /> */}
          {/* <Divider /> */}
          {buttons()}
        </FormBuilder.Section>
      </form>
    </Stack>
  );
};

export default React.memo(EmployeeConfig);
